import { ADMIN_URL } from '@synergy/config';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { LeafIcon } from '@synergy/theme-next';
import { Link, Stack, Tooltip, styled } from '@mui/material';
import { NorthEast } from '@mui/icons-material';
import { PoolActionLabel, PoolRow, PoolTooltip, PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import urlJoin from 'url-join';

import { VerticalDivider } from '../../../components/vertical-divider/vertical-divider';
import { useAppSelector } from '../../../../store/store';
import { useArticleDetailContext } from '../article-detail';

const StyledStack = styled(Stack)(({ theme }) => ({
    ...theme.typography.body2,
    flexWrap: 'wrap',
    color: theme.palette.grey[800],
    flexDirection: 'row',
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(1),
}));

export const ArticleDetailTitle = (): ReactElement | null => {
    const { article } = useArticleDetailContext();
    const { name, brand, globalTradeItemNumber, number, manufacturerNumber } = article;
    // The server says whether the signed-in person belongs to POOLALPIN itself;
    // for everyone else the link is not rendered at all (#2332).
    const isPoolEmployee = useAppSelector(state => !!state.configuration.isPoolEmployee);

    return (
        <Stack gap={{ xs: 0.5, sm: 1 }} alignItems="flex-start">
            <PoolTypography variant="h3">{name}</PoolTypography>
            <StyledStack divider={<VerticalDivider />}>
                {brand && (
                    <span>
                        <FormattedMessage id="app.article.detail.brand" /> {brand}
                    </span>
                )}
                <FormattedMessage id="app.article.detail.articleNumber" values={{ number }} />
                {globalTradeItemNumber && (
                    <span>
                        <FormattedMessage id="app.article.detail.gtin" /> {globalTradeItemNumber}
                    </span>
                )}
                {manufacturerNumber && (
                    <span>
                        <FormattedMessage id="app.article.detail.manufacturerNumberShort" /> {manufacturerNumber}
                    </span>
                )}
                {isPoolEmployee && (
                    <Link
                        href={urlJoin(ADMIN_URL, 'products/product', String(article.id), 'overview')}
                        target="_blank"
                        rel="noopener"
                        color="inherit"
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <FormattedMessage id="app.article.detail.openInPalo" />
                        <NorthEast sx={{ fontSize: 14 }} />
                    </Link>
                )}
            </StyledStack>
            {article.isAvailable && (
                <PoolRow gap={1} sx={{ mt: { xs: 1.5, sm: 1 } }}>
                    {!!article.specialPrice && (
                        <PoolTooltip
                            title={
                                <FormattedMessage
                                    id="app.specialOffer.tooltip"
                                    values={{
                                        date: (
                                            <FormattedDate
                                                value={article.specialPrice.endDate}
                                                year="numeric"
                                                month="short"
                                                day="2-digit"
                                            />
                                        ),
                                    }}
                                />
                            }
                        >
                            <PoolActionLabel variant="small" color="error" hasSkew={false}>
                                <FormattedMessage id="app.article.specialPrice" />
                            </PoolActionLabel>
                        </PoolTooltip>
                    )}
                    {article.isBestPrice && (
                        <PoolActionLabel variant="small" color="secondary" hasSkew={false}>
                            <FormattedMessage id="app.article.detail.bestPrice" />
                        </PoolActionLabel>
                    )}
                    {article.isEcoProduct && (
                        <Tooltip title={<FormattedMessage id="app.tooltip.ecoProduct" />}>
                            <Stack>
                                <PoolActionLabel variant="small" color="success" hasSkew={false}>
                                    <LeafIcon sx={{ fontSize: 16 }} />
                                </PoolActionLabel>
                            </Stack>
                        </Tooltip>
                    )}
                </PoolRow>
            )}
        </Stack>
    );
};
